import type { LayoutElement } from "@jod/schemas";
import {
  Button,
  FloorPlan,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@jod/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Ban, Flag, GlassWater, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { customerBackendApi } from "../features/api/backend.ts";
import { useRoomLayout } from "../features/floor-plan/use-room-layout.ts";
import { useT } from "../features/i18n/index.ts";
import { useSession } from "../features/session/session-context.tsx";
import { mockTables } from "../mocks/data.ts";

export const Route = createFileRoute("/room")({
  component: RoomScreen,
});

type TableEl = Extract<LayoutElement, { kind: "table" }>;

function RoomScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { tableCode, toast, blockTable, reportTable } = useSession();
  const [aroundOn, setAroundOn] = useState(false);
  const [target, setTarget] = useState<TableEl | null>(null);

  const backendEnabled = customerBackendApi !== null;
  const aroundQuery = useQuery({
    queryKey: ["customer", "around"],
    queryFn: () =>
      customerBackendApi?.listAroundTables() ??
      Promise.reject(new Error("Customer backend API unavailable")),
    enabled: backendEnabled,
    refetchInterval: 10_000,
  });

  const aroundTables = useMemo(() => {
    if (backendEnabled) return aroundQuery.data ?? [];
    return mockTables
      .filter((t) => t.mode !== "private")
      .map((t) => ({
        tableId: t.id,
        tableSessionId: t.id,
        code: t.code,
        label: t.zone,
        partyId: `party-${t.code}`,
        partySize: t.members.length,
        acceptingGifts: true,
        acceptingContactRequests: true,
        isOwnTable: false,
      }));
  }, [backendEnabled, aroundQuery.data]);

  const { layout, ownTable, markerIds, resolveAroundTarget, isLoading } = useRoomLayout(
    tableCode,
    aroundTables,
  );

  const visibleMarkerIds = aroundOn ? markerIds : [];

  return (
    <div className="relative flex min-h-dvh flex-col bg-background">
      <header className="absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-2 px-4 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-3">
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate({ to: "/home" })}
          className="flex size-10 items-center justify-center rounded-full border border-hairline bg-glass text-cream backdrop-blur"
        >
          <ArrowLeft className="size-5" />
        </button>
        <div className="text-center">
          <p className="font-display font-semibold text-sm">{layout.name}</p>
          <p className="text-cream-3 text-xs">
            {isLoading ? t("room.loadingFloor") : t("room.floorTonight")}
          </p>
        </div>
        <button
          type="button"
          aria-pressed={aroundOn}
          onClick={() => setAroundOn((v) => !v)}
          className={`flex size-10 items-center justify-center rounded-full border backdrop-blur transition-colors ${
            aroundOn ? "border-lime bg-lime/20 text-lime" : "border-hairline bg-glass text-cream-3"
          }`}
          aria-label="Toggle who's around"
        >
          <Users className="size-5" />
        </button>
      </header>

      <div className="flex-1 p-2 pt-[calc(env(safe-area-inset-top)+4.5rem)]">
        <FloorPlan
          layout={layout}
          highlightIds={ownTable ? [ownTable.id] : []}
          markerIds={visibleMarkerIds}
          onElementClick={(_id, el) => {
            if (el.kind === "table" && el.id !== ownTable?.id) setTarget(el);
          }}
        />
      </div>

      <p className="px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] text-center text-cream-3 text-xs">
        {ownTable ? (
          <>
            {t("room.yourTablePrefix")}{" "}
            <span className="text-lime">{ownTable.label ?? ownTable.code}</span> ·{" "}
            {t("room.tapOtherToSend")}
          </>
        ) : (
          t("room.tapToSend")
        )}
      </p>

      <Sheet open={target !== null} onOpenChange={(open) => !open && setTarget(null)}>
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle>{target?.label ?? target?.code}</SheetTitle>
            <SheetDescription>{t("room.sheetDesc")}</SheetDescription>
          </SheetHeader>
          <div className="mt-2 flex flex-col gap-2">
            <Button
              className="gap-2"
              onClick={() => {
                if (!target) return;
                const around = resolveAroundTarget(target);
                const giftCode = around?.code ?? target.code;
                setTarget(null);
                navigate({ to: "/send-drink", search: { to: giftCode } });
              }}
            >
              <GlassWater className="size-4" />
              {t("room.sendDrink")}
            </Button>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                className="flex-1 gap-2"
                onClick={() => {
                  if (!target) return;
                  const around = resolveAroundTarget(target);
                  blockTable(target.code, around?.partyId);
                  toast(t("table.blockedToast"));
                  setTarget(null);
                }}
              >
                <Ban className="size-4" />
                {t("common.block")}
              </Button>
              <Button
                variant="secondary"
                className="flex-1 gap-2"
                onClick={() => {
                  if (!target) return;
                  const around = resolveAroundTarget(target);
                  reportTable(target.code, "room_floor_tap", around?.partyId);
                  toast(t("room.reportToast"));
                  setTarget(null);
                }}
              >
                <Flag className="size-4" />
                {t("common.report")}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
